import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Skeleton,
  Pagination,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import Page from 'src/components/Page';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PrivacyPolicyModal from 'src/sections/PrivacyPolicyModal';
import PrivacyPolicyViewModal from 'src/sections/dashboard-sections/PrivacyPolicyViewModal';
import { getPrivacy, addPrivacy, deletePrivacy, makeActivePrivacy } from 'src/ApiServices';

export default function PrivacyPolicy() {
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bestModalOpen, setBestModalOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedPolicyForBest, setSelectedPolicyForBest] = useState(null);
  const [currentBestStatus, setCurrentBestStatus] = useState(false);
  const [bestLoading, setBestLoading] = useState(false);
  const [policiesData, setPoliciesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });

  const fetchPoliciesData = async (currentPage = 1) => {
    try {
      setLoading(true);
      const response = await getPrivacy({ page: currentPage });
      const transformedData = transformApiData(response.data || []);
      setPoliciesData(transformedData);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: response.data?.length || 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Error fetching privacy policies:', error);
      setPoliciesData([]);
      toast.error('Failed to load privacy policies');
    } finally {
      setLoading(false);
    }
  };

  const extractTextFromHTML = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.replace(/\s+/g, ' ').trim();
  };

  const extractTitleFromHTML = (html) => {
    const text = extractTextFromHTML(html);
    if (text.length <= 50) return text;
    return text.substring(0, 50) + '...';
  };

  const transformApiData = (apiData) => {
    return apiData
      .map((policy) => {
        let contents = [];

        if (policy.value) {
          try {
            if (policy.value.startsWith('[')) {
              const parsedValue = JSON.parse(policy.value);
              if (Array.isArray(parsedValue)) {
                contents = parsedValue.map((item, index) => ({
                  id: index,
                  paragraph: item.paragraph || '',
                  type: item.type || 'paragraph'
                }));
              }
            } else {
              contents = [
                {
                  id: 0,
                  paragraph: policy.value,
                  type: 'paragraph'
                }
              ];
            }
          } catch (error) {
            console.error('Error parsing policy value:', error);
            contents = [
              {
                id: 0,
                paragraph: policy.value,
                type: 'paragraph'
              }
            ];
          }
        }

        const title = policy.company_policy || extractTitleFromHTML(contents[0]?.paragraph || '');

        return {
          id: policy.id,
          main_id: policy.id,
          title: title,
          status: policy.status === 'active' ? 'Active' : 'Inactive',
          isActive: policy.status === 'active',
          is_best: parseInt(policy.is_best) || 0,
          created_at: policy.created_at,
          updated_at: policy.updated_at,
          contents: contents,
          company_policy: policy.company_policy,
          value: policy.value,
          type: policy.type
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
  };

  useEffect(() => {
    fetchPoliciesData(page);
  }, [page]);

  const handleAddClick = () => {
    setSelectedPolicy(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleEditClick = (policy) => {
    setSelectedPolicy(policy);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleViewClick = (policy) => {
    setSelectedPolicy(policy);
    setViewModalOpen(true);
  };

  const handleDeleteClick = (policy) => {
    setSelectedPolicy(policy);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPolicy) return;
    try {
      setDeleteLoading(true);
      await deletePrivacy(selectedPolicy.id);
      toast.success('Privacy policy deleted successfully!');
      fetchPoliciesData(page);
      setDeleteModalOpen(false);
      setSelectedPolicy(null);
    } catch (error) {
      console.error('Failed to delete policy:', error);
      toast.error(error.response?.data?.message || 'Failed to delete policy!');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedPolicy(null);
  };

  // Best Property Toggle Logic
  const handleBestToggle = (policy) => {
    const policyId = policy.id;
    const currentBestStatus = policy.is_best === 1;

    if (currentBestStatus) {
      performBestToggle(policy, currentBestStatus);
      return;
    }

    const existingBestPolicy = policiesData.find((p) => p.is_best === 1 && p.id !== policyId);

    if (existingBestPolicy) {
      setSelectedPolicyForBest(policy);
      setCurrentBestStatus(currentBestStatus);
      setBestModalOpen(true);
    } else {
      performBestToggle(policy, currentBestStatus);
    }
  };

  const performBestToggle = async (policy, currentBestStatus) => {
    const policyId = policy.id;
    const newBestStatus = currentBestStatus ? 0 : 1;
    const previousPolicies = [...policiesData];

    setPoliciesData((prev) =>
      prev.map((p) => ({
        ...p,
        is_best: p.id === policyId ? newBestStatus : newBestStatus ? 0 : p.is_best
      }))
    );

    try {
      await makeActivePrivacy(policyId, newBestStatus);
      toast.success(`Policy ${newBestStatus ? 'marked as' : 'removed from'} Best Policy!`);
      await fetchPoliciesData(page);
    } catch (error) {
      setPoliciesData(previousPolicies);
      toast.error('Failed to update Best Policy status');
      console.error('Error saving best policy:', error);
    }
  };

  const handleBestConfirm = async () => {
    if (!selectedPolicyForBest) return;

    try {
      setBestLoading(true);
      await performBestToggle(selectedPolicyForBest, currentBestStatus);
      setBestModalOpen(false);
      setSelectedPolicyForBest(null);
    } catch (error) {
      console.error('Failed to update best policy:', error);
    } finally {
      setBestLoading(false);
    }
  };

  const handleBestCancel = () => {
    setBestModalOpen(false);
    setSelectedPolicyForBest(null);
    setCurrentBestStatus(false);
  };

  const handleModalSubmit = async (formData) => {
    try {
      const policyDetailsArray = formData.contents.map((contentItem) => {
        return {
          paragraph: contentItem.content,
          type: contentItem.type.toLowerCase().replace(' point', '')
        };
      });

      const payloadObject = {
        heading: extractTitleFromHTML(formData.contents[0]?.content || ''),
        value: JSON.stringify(policyDetailsArray)
      };

      if (isEditing && selectedPolicy) {
        payloadObject.id = selectedPolicy.id;
      }

      await addPrivacy(payloadObject);
      toast.success(isEditing ? 'Policy updated successfully!' : 'Policy added successfully!');

      fetchPoliciesData(page);
      setModalOpen(false);
    } catch (error) {
      console.error('Error saving policy:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors ||
        `Failed to ${isEditing ? 'update' : 'add'} policy`;
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const tableHeaders = ['Title', 'Content', 'Status', 'Mark as Active', 'Actions'];

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        {tableHeaders.map((_, cellIndex) => (
          <TableCell key={cellIndex} sx={{ px: 2 }}>
            <Skeleton variant="text" width={cellIndex === 1 ? 200 : 120} height={25} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderNoRecordsFound = () => (
    <TableRow>
      <TableCell
        colSpan={tableHeaders.length}
        sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
          >
            No Privacy Policies Found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[600],
              fontFamily: 'poppinsRegular',
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            No privacy policies available. Add new policies to get started.
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Page title={`Privacy Policy | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Privacy Policy
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              minWidth: { xs: '100%', sm: 160 },
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Add Policy
          </Button>
        </Box>

        <Card sx={{ overflow: 'auto' }}>
          <TableContainer
            sx={{
              overflowX: 'auto',
              width: '100%'
            }}
          >
            <Table
              sx={{
                minWidth: 1600,
                '& .MuiTableCell-head': {
                  backgroundColor: palette.background.paper,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsSemiBold',
                  fontSize: '14px',
                  borderBottom: `2px solid ${palette.divider}`,
                  py: 2
                },
                '& .MuiTableCell-body': {
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '14px',
                  borderBottom: `1px solid ${palette.divider}`,
                  py: 2
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableCell key={index} sx={{ px: 2, whiteSpace: 'nowrap' }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? renderSkeletonRows()
                  : policiesData.length > 0
                  ? policiesData.map((policy) => {
                      const title =
                        policy.company_policy ||
                        extractTitleFromHTML(policy.contents[0]?.paragraph || '');
                      const allContent = policy.contents
                        .map((item) => extractTextFromHTML(item.paragraph))
                        .join(' ');
                      const contentPreview =
                        allContent.length > 100 ? allContent.substring(0, 100) + '...' : allContent;

                      return (
                        <TableRow
                          key={policy.id}
                          sx={{ '&:hover': { backgroundColor: palette.action.hover } }}
                        >
                          <TableCell sx={{ px: 2 }}>{title}</TableCell>
                          <TableCell sx={{ px: 2 }}>{contentPreview}</TableCell>
                          <TableCell sx={{ px: 2 }}>{policy.status}</TableCell>
                          <TableCell sx={{ px: 2, textAlign: 'center' }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={policy.is_best === 1}
                                  onChange={() => handleBestToggle(policy)}
                                  color="primary"
                                />
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ px: 2, textAlign: 'center' }}>
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleViewClick(policy)}
                                sx={{ color: palette.info.main, mr: 1 }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(policy)}
                                sx={{ color: palette.primary.main, mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(policy)}
                                sx={{ color: palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  : renderNoRecordsFound()}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {!loading && policiesData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3, overflow: 'auto' }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        <PrivacyPolicyModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleModalSubmit}
          policy={selectedPolicy}
          isEditing={isEditing}
        />

        <PrivacyPolicyViewModal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          policy={selectedPolicy}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          PaperProps={{ sx: { borderRadius: 2, padding: 1, m: 2 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmberIcon sx={{ fontSize: 60, color: palette.warning.main, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to delete this privacy policy?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3, flexWrap: 'wrap' }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                minWidth: 100,
                '&:hover': { backgroundColor: palette.error.dark }
              }}
              disabled={deleteLoading}
              startIcon={<DeleteIcon />}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Best Property Confirmation Modal */}
        <Dialog
          open={bestModalOpen}
          onClose={handleBestCancel}
          PaperProps={{ sx: { borderRadius: 2, padding: 1, m: 2 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmberIcon sx={{ fontSize: 60, color: palette.warning.main, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Change Best Policy
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to mark <strong>{selectedPolicyForBest?.title}</strong> as
              Active Policy?
              <br />
              The current Active policy will be automatically replaced.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3, flexWrap: 'wrap' }}>
            <Button
              onClick={handleBestCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={bestLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBestConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.primary.main,
                color: palette.primary.contrastText,
                minWidth: 100,
                '&:hover': { backgroundColor: palette.primary.dark }
              }}
              disabled={bestLoading}
            >
              {bestLoading ? 'Updating...' : 'Mark as Best'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
